import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { NoteVersionHistoryComponent, noteVersionError } from '../../version-history/note-version-history.component';
import { NOTE_HISTORY_INLINE_HOST, NoteVersionHistoryOpener } from '../../version-history/note-version-history.opener';
import type { NoteSummaryDto } from '@sharedlib/dtos/dto/notes-app/note.dto';
import { isEqual } from 'lodash-es';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import {
  ITEM_REVIEW_INLINE_HOST,
  type ItemReviewTarget,
} from '@modules/common/components/item-review/item-review-dialog.service';
import { ElementRef } from '@angular/core';
import { type SimpleChanges } from '@angular/core';
import { EditorComponent } from '@modules/form-controls/components/editor/editor.component';
import { ItemHandoverComponent } from '@modules/common/components/item-handover/item-handover.component';
import {
  ITEM_HANDOVER_INLINE_HOST,
  type ItemHandoverTarget,
} from '@modules/common/components/item-handover/item-handover-dialog.service';
import { UILocations } from '@modules/common/types/uiLocations';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { ItemQuickCommentComponent } from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { DetailBreadcrumbsComponent, type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { TitleByNotesFilterPipe } from '@modules/notes/pipes/title-by-notes-filter.pipe';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  Injector,
  Input,
  OnInit,
  signal,
  type TemplateRef,
  afterNextRender,
  viewChild,
  untracked,
} from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { NoteCollaborationControlsComponent } from '@modules/notes/collaboration/note-collaboration-controls.component';
import {
  NoteAccessPanelComponent,
  type NoteAccessView,
} from '@modules/notes/collaboration/note-access-panel.component';
import {
  NoteOwningWorkspaceService,
  type NoteOwningWorkspace,
} from '@modules/notes/services/note-owning-workspace.service';
import { NoteDetailHeaderComponent } from './note-detail-header.component';
import { NoteActivityComponent } from '@modules/notes/collaboration/note-activity.component';
import type { NoteCollaborationDto, NoteTeamAccess } from '@sharedlib/dtos/dto/notes-app/note-collaboration.dto';
import { type WorkspaceIdentity, WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { Application } from '@modules/common/types/application';
import { type BaseSearchResponse } from '@modules/common/types/base-search-response';
import { NotebookSummariesCache } from '@modules/notes/services/notebook-summaries.cache';
import { type AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { warmUpObservable } from '@frontendlib/common/decorators/warm-up-observable';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { InputComponent } from '@frontendlib/components/controls/input/input.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { SearchLocationBadgeComponent } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { AlertService } from '@modules/alert/services/alert.service';
import { CommentsModule } from '@modules/comments/comments.module';
import { FeatureUpsellComponent } from '@modules/common/components/feature-upsell/feature-upsell.component';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { ItemDescriptionFieldComponent } from '@modules/common/components/item-description/item-description-field.component';
import { StitchTimestampsComponent } from '@modules/common/components/stitch-timestamps/stitch-timestamps.component';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { PreventDirtyClickDirective } from '@modules/common/directives/prevent-dirty-click.directive';
import { Tab } from '@modules/common/types/tab';
import { Upload } from '@modules/common/types/upload';
import { ConnectionsComponent } from '@modules/connections/components/connections/connections.component';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { StitchHubComponent } from '@modules/elements/components/stitch-hub/stitch-hub.component';
import { SaveCancelPanelComponent } from '@modules/elements/components/stitch-save-cancel-panel/stitch-save-cancel-panel.component';
import { NoteContextMenuDirective } from '@modules/elements/directives/note-context-menu.directive';
import { NoteReadOnlyContextMenuDirective } from '@modules/notes/collaboration/note-read-only-context-menu.directive';
import { ElementsModule } from '@modules/elements/elements.module';
import { AutocompleteInputComponent } from '@modules/form-controls/components/autocomplete-input/autocomplete-input.component';
import { ButtonComponent } from '@modules/form-controls/components/button/button.component';
import { type ActionsMenuAction } from '@modules/form-controls/components/editor/tools/actions-menu.tool';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { KnotsComponent } from '@modules/knots/components/knots/knots.component';
import { KnotsService } from '@modules/knots/services/knots.service';
import { Knot } from '@modules/knots/types/knot';
import { KnowledgeHubComponent } from '@modules/knowledge/components/knowledge-hub/knowledge-hub.component';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { Note, type NoteFormGroupConfig } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { ContainerBadgeComponent } from '@modules/common/components/container-badge/container-badge.component';
import { ScrollModule } from '@modules/scroll/scroll.module';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { TagsComponent } from '@modules/tags/components/tags/tags.component';
import { TagsService } from '@modules/tags/services/tags.service';
import { Tag } from '@modules/tags/types/tag';
import { ResizeDirective } from '@modules/utils/directives/resize/resize.directive';
import { ToArrayPipe } from '@modules/utils/pipes/array/to-array.pipe';
import type { AccountAppSubjectsType } from '@sharedlib/common/permissions/subjects/AccountAppSubjects';
import { type Actions } from '@sharedlib/common/permissions/types/Permissions';
import { SignalStitchEventType, SignalType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import {
  catchError,
  EMPTY,
  expand,
  finalize,
  filter,
  from,
  map,
  merge,
  Observable,
  ReplaySubject,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
} from 'rxjs';
import { CommonModule as AppCommonModule } from '../../../common/common.module';

@Component({
  selector: 'app-note-form',
  templateUrl: './note-form.component.html',
  styleUrls: ['./note-form.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    WorkspaceIdentityService,
    NoteOwningWorkspaceService,
    {
      provide: NOTE_HISTORY_INLINE_HOST,
      useFactory: () => {
        const form = inject(NoteFormComponent);
        return { open: (note: NoteSummaryDto): boolean => form.openVersionHistory(note) };
      },
    },
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(NoteFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    {
      provide: ITEM_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(NoteFormComponent);
        return { open: (target: ItemHandoverTarget): boolean => form.openInlineAssignment(target) };
      },
    },
  ],
  imports: [
    DetailBreadcrumbsComponent,
    ContainerBadgeComponent,
    NoteVersionHistoryComponent,
    NgTemplateOutlet,
    ItemHandoverComponent,
    ItemReviewComponent,
    ItemWorkspaceFooterComponent,
    ItemQuickCommentComponent,
    ItemTeamLabelComponent,
    SearchLocationBadgeComponent,
    ReactiveFormsModule,
    MatTooltipModule,
    AppCommonModule,
    CommentsModule,
    DragNDropModule,
    ElementsModule,
    FormControlsModule,
    ContextMenuStateIndicatorComponent,
    ScrollModule,
    InputComponent,
    QuickActionComponent,
    Button2Component,
    ButtonComponent,
    IconComponent,
    PopoverDirective,
    PreventDirtyClickDirective,
    NoteContextMenuDirective,
    NoteReadOnlyContextMenuDirective,
    TabsComponent,
    StitchTimestampsComponent,
    TagsComponent,
    KnotsComponent,
    ConnectionsComponent,
    AutocompleteInputComponent,
    ItemDescriptionFieldComponent,
    LinkedInfoListComponent,
    EntityHubSearchComponent,
    StitchHubComponent,
    SaveCancelPanelComponent,
    KnowledgeHubComponent,
    FeatureUpsellComponent,
    ToArrayPipe,
    ResizeDirective,
    NoteCollaborationControlsComponent,
    NoteAccessPanelComponent,
    NoteDetailHeaderComponent,
    NoteActivityComponent,
  ],
})
export class NoteFormComponent
  extends FullFormBaseComponent<StitchType.NOTE, Note, NoteFormGroupConfig>
  implements OnInit
{
  protected readonly StitchType = StitchType;
  public readonly reviewApi = inject(NotesService).reviewApi;

  /**
   * Escape closes a note window nobody changed (#1598, the prototype's windows); a changed note never
   * closes on a key (R4), and an open popover such as the notebook picker owns its own Escape. A popover
   * leaves focus on its trigger inside this form and handles the key through the overlay keyboard
   * dispatcher on <body>, which sees it AFTER this host — so the decision waits until the key has
   * finished dispatching and the popover has had its say.
   */
  @HostListener('keydown.escape', ['$event'])
  protected handleEscape(event: Event): void {
    if (event.defaultPrevented || !this.pristine) return;
    if (event.target instanceof Element && event.target.closest('.cdk-overlay-container')) return;
    // EditorJS closes its own toolbox, block tunes, inline toolbar and block selection on Escape without
    // preventing it, so an open one of those keeps the note open (they all render as .ce-popover--opened).
    const host = event.currentTarget instanceof Element ? event.currentTarget : null;
    if (host?.querySelector('.ce-popover--opened, .ce-block--selected')) return;
    setTimeout(() => {
      if (!event.defaultPrevented && this.pristine) this.handleClose();
    });
  }
  private readonly reviewControl = viewChild(ItemReviewComponent);
  public openInlineReview(target: ItemReviewTarget): boolean {
    const control = this.reviewControl();
    if (
      !this.handoverInsideModal() ||
      !control ||
      target.itemId !== this.note?.id ||
      target.tenantId !== this.handoverTenantId()
    )
      return false;
    control.openRequest();
    return true;
  }
  public readonly handoverApi = inject(NotesService).handoverApi;
  private readonly handoverControl = viewChild(ItemHandoverComponent);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly handoverInsideModal = computed(
    () => !!this.hostElement.nativeElement.closest('app-full-form-modal'),
  );
  /**
   * The team that owns this saved note, or undefined. The ambient identity only knows the team the window was opened
   * from; a note window opened with no ambient team (a modal, Updates) is still a team note when the note's own
   * owning workspace resolved, which is the same fact the collaboration controls rely on.
   */
  protected readonly handoverTenantId = computed(() => {
    this.workspaceRevision();
    const note = this.note;
    if (!note?.id || !this.accountKind.isTeam()) return undefined;
    if (itemWorkspace(this.workspaceIdentity(), note) === 'team') return note.accountId;
    const owning = this.collaborationTenantId();
    return owning && owning === note.accountId ? owning : undefined;
  });
  private readonly handoverPermissions: PermissionsService<Actions, AccountAppSubjectsType> = inject(PermissionsService);
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  private readonly accountKind = inject(AccountKindService);
  /** Space Documents update AND this note's own edit capability: the rule the header's Archive and Trash already obey. */
  protected readonly canAssignHandover = computed(
    () => canEditDocumentsIn(this.handoverPermissions, this.handoverTenantId()) && !this.contentReadOnly(),
  );
  public openInlineAssignment(target: ItemHandoverTarget): boolean {
    const control = this.handoverControl();
    if (
      !this.canAssignHandover() ||
      !this.handoverInsideModal() ||
      !control ||
      target.itemId !== this.note?.id ||
      target.tenantId !== this.handoverTenantId()
    )
      return false;
    control.openAssign();
    return true;
  }
  protected readonly UILocations = UILocations;
  protected readonly workspaceRevision = itemChangeRevision(
    merge(this.stitchItemChanged, this.itemUpdated.pipe(map(() => undefined))),
  );
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));

  private readonly alertService = inject(AlertService);
  private readonly document = inject(DOCUMENT);
  private readonly noteToasterService = inject(ToasterService);
  private readonly notesAppStateService = inject(NotesAppStateService);
  private readonly notebookSummariesCache = inject(NotebookSummariesCache);

  protected readonly footerBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.notebookId,
    cache: {
      get: (id) =>
        this.notebookSummariesCache.getForItem(computed(() => ({ id: id(), accountId: this.stitchItem?.accountId }))),
    },
    badgeType: 'notebook',
    shown: computed(() => !!this.displayWorkspaceIdentity()),
  });
  /** Which notebook the open item is filed under; see {@link headerLocationBadge}. */
  protected readonly locationBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.notebookId,
    cache: {
      get: (id) =>
        this.notebookSummariesCache.getForItem(computed(() => ({ id: id(), accountId: this.stitchItem?.accountId }))),
    },
    search: inject(AdvancedSearchService),
    application: Application.notes,
    badgeType: 'notebook',
    alsoWhen: this.openedFromHub,
  });

  private readonly titleByNotesFilter = new TitleByNotesFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(NotesAppStateService, {
    appLabel: APP_CRUMBS.notes,
    viewTitle: (folder) => this.titleByNotesFilter.transform(folder, null, true),
  });

  /** [workspace ›] Notes app › the list's view › this note. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      container: this.headerNotebook()?.title,
      fallbackView: 'Notes',
      itemTitle: this.note?.title || 'Untitled note',
      itemId: this.note?.id,
    });
  }

  /** The notebook this note sits in, named by the header's container badge. */
  protected readonly headerNotebook = this.notebookSummariesCache.getForItem(
    computed(() => {
      this.workspaceRevision();
      const note = this.stitchItem;
      return note?.notebookId ? { id: note.notebookId, accountId: note.accountId } : undefined;
    }),
  );

  protected readonly highlights = signal<string[]>([]);
  public changesKey = 'note';
  private readonly baseTabs: Tab[] = [
    { title: 'Note', value: 'note' },
    { title: 'Details', value: 'details' },
    { title: 'Hub', value: 'hub' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  public override tabs: Tab[] = this.baseTabs;
  private readonly tabsView = viewChild(TabsComponent);
  protected readonly visibleTabs = computed<Tab[]>(() => {
    const tabs = this.collaborationState()
      ? [...this.tabsWithCounts(), { title: 'Activity', value: 'activity' }]
      : this.tabsWithCounts();
    return tabs;
  });
  private readonly historyOpener = inject(NoteVersionHistoryOpener);
  protected readonly historyAvailable = computed(() => {
    this.workspaceRevision();
    return (
      !!this.note?.id &&
      this.spaceContextStore.insideTeam() &&
      this.note.accountId === this.spaceContextStore.contextId()
    );
  });
  protected readonly historySaveRequired = signal(false);
  protected readonly historySaveFailure = signal<string | null>(null);
  protected readonly historyRestorePending = signal(false);
  private readonly historyCancelButton = viewChild<unknown, ElementRef<HTMLElement>>('historySaveCancel', {
    read: ElementRef,
  });
  private readonly historyEditor = viewChild<EditorComponent, ElementRef<HTMLElement>>(EditorComponent, {
    read: ElementRef,
  });
  private historyFocusReturn?: HTMLElement;
  private readonly focusHistoryCancel = effect(() => {
    const button = this.historyCancelButton();
    if (!button) return;
    afterNextRender(
      () => {
        if (this.historyCancelButton() === button)
          button.nativeElement.querySelector<HTMLButtonElement>('button')?.focus();
      },
      { injector: this.injector },
    );
  });
  private historyContinuation?: () => void;
  private historyGeneration = 0;
  private readonly historyIntent = effect(() => {
    // Availability may remain true while the main view binds another team note.
    this.workspaceRevision();
    const request = this.historyOpener.requested();
    const available = this.historyAvailable();
    const contextId = request ? this.spaceContextStore.contextId() : null;
    const insideTeam = request ? this.spaceContextStore.insideTeam() : false;
    untracked(() => {
      if (!available) {
        if (this.selectedTab === 'versions') this.selectedTab = 'note';
        this.cancelHistorySave();
      }
      if (!request) return;
      if (!insideTeam || request.note.accountId !== contextId) {
        this.historyOpener.requested.set(null);
        return;
      }
      if (request.note.id !== this.note?.id || request.note.accountId !== this.note?.accountId) return;
      this.historyOpener.requested.set(null);
      if (available) this.openVersionHistory(this.note);
    });
  });
  private needsHistorySave(): boolean {
    // handleEditorContentEdited marks the body dirty before its debounced value arrives.
    return !this.pristine;
  }
  public openVersionHistory(note: NoteSummaryDto): boolean {
    if (!this.historyAvailable() || note.id !== this.note.id || note.accountId !== this.note.accountId) return false;
    this.beforeHistoryAction(() => {
      this.selectedTab = 'versions';
      this.noteChangeDetector.markForCheck();
    });
    return true;
  }
  private tabSelectionRevision = 0;

  protected async selectHistoryTab(value: string): Promise<void> {
    if (this.historyRestorePending()) return;
    const revision = ++this.tabSelectionRevision;
    if (value === 'versions') this.openVersionHistory(this.note);
    else {
      const editor = this.selectedTab === 'note' ? this.bodyEditor() : undefined;
      if (editor && value !== 'note' && this.form.controls.body.dirty && !editor.readOnly()) {
        const note = this.note;
        const form = this.form;
        try {
          const flushed = await editor.flush();
          if (
            this.destroyRef.destroyed ||
            revision !== this.tabSelectionRevision ||
            this.note !== note ||
            this.form !== form
          )
            return;
          if (!flushed) {
            this.showEditorNotReady();
            return;
          }
        } catch {
          this.showEditorNotReady();
          return;
        }
      }
      this.cancelHistorySave();
      this.selectedTab = value;
      this.noteChangeDetector.markForCheck();
    }
  }
  protected readonly prepareHistoryRestore = (continueRestore: () => void): boolean => {
    if (!this.needsHistorySave()) return true;
    this.beforeHistoryAction(continueRestore);
    return false;
  };
  private beforeHistoryAction(action: () => void): void {
    const note = this.note;
    const generation = this.historyGeneration;
    const continueAction = (): void => {
      if (this.note !== note || generation !== this.historyGeneration || !this.historyAvailable()) return;
      if (this.needsHistorySave()) {
        this.historyContinuation = action;
        const editor = this.historyEditor()?.nativeElement;
        const active = this.document.activeElement;
        const anchor = this.document.getSelection()?.anchorNode;
        const selected = (anchor instanceof HTMLElement ? anchor : anchor?.parentElement)?.closest<HTMLElement>(
          '[contenteditable="true"]',
        );
        this.historyFocusReturn =
          active instanceof HTMLElement && editor?.contains(active) && active.matches('[contenteditable="true"]')
            ? active
            : selected && editor?.contains(selected)
              ? selected
              : (editor?.querySelector<HTMLElement>('[contenteditable="true"]') ?? undefined);
        this.historySaveRequired.set(true);
      } else action();
    };
    const pending = this.pendingSave();
    if (pending) pending.pipe(take(1), takeUntil(this.alive)).subscribe(continueAction);
    else continueAction();
  }
  protected saveBeforeHistory(): void {
    if (this.editorSubmitInProgress() || this.saveInProgress) return;
    const note = this.note;
    const generation = this.historyGeneration;
    let saved = false;
    this.historySaveFailure.set(null);
    this.handleSubmit()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe({
        next: () => {
          saved = true;
        },
        error: (error: unknown) => this.historySaveFailure.set(noteVersionError(error)),
        complete: () =>
          queueMicrotask(() => {
            if (!saved || this.note !== note || generation !== this.historyGeneration || this.needsHistorySave())
              return;
            const action = this.historyContinuation;
            this.cancelHistorySave();
            this.noteChangeDetector.markForCheck();
            // Let the history input observe the finished save before invoking its guarded restore.
            afterNextRender(
              () => {
                if (
                  this.note === note &&
                  generation === this.historyGeneration &&
                  this.historyAvailable() &&
                  !this.needsHistorySave()
                )
                  action?.();
              },
              { injector: this.injector },
            );
          }),
      });
  }
  protected cancelHistorySave(restoreFocus = false): void {
    const focus = this.historyFocusReturn;
    this.historySaveRequired.set(false);
    this.historySaveFailure.set(null);
    this.historyContinuation = undefined;
    this.historyFocusReturn = undefined;
    if (restoreFocus && focus?.isConnected) focus.focus();
  }
  protected applyHistoryNote(fresh: Note): void {
    if (
      !this.historyAvailable() ||
      this.note.id !== fresh.id ||
      this.note.accountId !== fresh.accountId ||
      this.needsHistorySave()
    )
      return;
    Object.assign(this.note, fresh);
    this.stitchItemChanged.next();
    this.itemUpdated.emit(this.note);
  }
  protected override attachmentsMenuStateVariant = 'notes';
  public readonly editorActions: ActionsMenuAction[];

  protected dragDataType = DragDataTypes.note;

  private readonly notebooksService = inject(NotebooksService);
  private readonly notesService = inject(NotesService);
  private readonly signalsService = inject(SignalsService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  public suggestions: AutocompleteFactory<Notebook> = this.notebooksService.getAutocompleteSuggestions();

  @Input() note = new Note();

  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly tagsService = inject(TagsService);
  private readonly knotsService = inject(KnotsService);
  private readonly notesTenantScopeService = inject(NotesTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);
  private readonly owningWorkspaceService = inject(NoteOwningWorkspaceService);
  protected readonly collaborationControls = viewChild(NoteCollaborationControlsComponent);
  private readonly saveLocationTrigger = viewChild<Button2Component>('saveLocationTrigger');
  private readonly savedWorkspace = signal<NoteOwningWorkspace | null | undefined>(undefined);
  protected readonly owningWorkspaceError = signal(false);
  protected readonly retryOwningWorkspace = new Subject<void>();
  private readonly savedTenantId = signal<string | null>(null);
  private readonly savedHasCollaboration = signal(false);
  protected readonly collaborationTenantId = computed(() =>
    this.savedTenantId()
      ? this.savedHasCollaboration() || this.savedWorkspace()
        ? this.savedTenantId()
        : null
      : this.destinationIsTeam()
        ? (this.workspaceIdentity()?.spaceTenantId ?? null)
        : null,
  );
  /** What the collaboration controls and their reads mount on: team workflows belong to team logins, whatever space the note lives in. */
  protected readonly collaborationControlsTenantId = computed(() =>
    this.accountKind.isTeam() ? this.collaborationTenantId() : null,
  );
  protected readonly collaborationTeamName = computed(() =>
    this.savedTenantId()
      ? (this.savedWorkspace()?.spaceTitle ?? 'Team Workspace')
      : (this.workspaceIdentity()?.spaceTitle ?? 'Team Workspace'),
  );
  protected readonly accessPanelView = computed<NoteAccessView>(
    () =>
      this.collaborationControls()?.accessView() ?? {
        owner: null,
        access: null,
        state: 'loading',
        editable: false,
        pending: false,
        error: null,
      },
  );
  private readonly newNotebookDialog = viewChild.required<TemplateRef<void>>('newNotebookDialog');

  /** True only for a form that began as New Note; it stays true after that form receives its id. */
  protected readonly creationJourney = signal(true);

  /** Flips before the created item is applied so the two location controls lock without a stale frame. */
  protected readonly savedInThisForm = signal(false);
  protected readonly initialAssigneeIds = signal<readonly string[]>([]);
  protected readonly initialTeamAccess = signal<NoteTeamAccess>('editor');
  protected readonly collaborationState = signal<NoteCollaborationDto | null | undefined>(undefined);
  protected readonly collaborationAccessLost = signal(false);
  protected updateCollaborationState(collaboration: NoteCollaborationDto | null | undefined): void {
    if (collaboration === null) this.selectNoteTab();
    this.collaborationAccessLost.set(false);
    this.collaborationState.set(collaboration);
  }
  private selectNoteTab(): void {
    if (this.selectedTab !== 'activity') return;
    this.tabsView()?.handleSelect(0);
    this.selectedTab = 'note';
  }
  protected readonly contentReadOnly = computed(() => {
    if (this.noteContentState() !== 'ready') return true;
    if (!this.stitchItem?.id) return false;
    if (this.collaborationAccessLost()) return true;
    const collaboration = this.collaborationState();
    if (collaboration !== undefined) return !!collaboration && !collaboration.capabilities.canEdit;
    if (this.stitchItem.collaboration) return true;
    if (this.savedTenantId() && this.savedWorkspace() === undefined) return true;
    if (this.collaborationTenantId()) return true;
    const teamTenantId =
      this.workspaceIdentity()?.spaceTenantId ??
      (this.spaceContextStore.insideTeam() ? this.spaceContextStore.contextId() : null);
    return !!teamTenantId && this.stitchItem.accountId === teamTenantId;
  });
  private collaborationNoteId: string | null = null;
  private resetUnsupportedActivity(note: Note | undefined): void {
    const identity = this.workspaceIdentity();
    const knownPrivate =
      note?.collaboration === null ||
      (note?.collaboration === undefined &&
        !!identity &&
        !!note?.accountId &&
        note?.accountId !== identity.spaceTenantId);
    if (!note?.id || knownPrivate) this.selectNoteTab();
  }
  private readonly contentAccessEffect = effect(() => {
    if (!this.form) return;
    if (this.contentReadOnly() || this.historyRestorePending()) {
      this.form.disable({ emitEvent: false });
      this.autoSaveAllowed = false;
    } else {
      this.form.enable({ emitEvent: false });
    }
  });

  /** The current tenant's menu rows; cleared before every tenant-specific request. */
  protected readonly notebookOptions = signal<Notebook[]>([]);
  protected readonly saveLocationMenuOpen = signal(false);
  protected readonly notebookMenuOpen = signal(false);
  protected readonly notebookCreateInProgress = signal(false);
  protected readonly newNotebookTitle = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  private readonly newNotebookTitleValue = toSignal(
    this.newNotebookTitle.valueChanges.pipe(startWith(this.newNotebookTitle.value)),
    { initialValue: '' },
  );
  protected readonly newNotebookSaveDisabled = computed(
    () => this.notebookCreateInProgress() || this.newNotebookTitleValue().trim().length === 0,
  );

  protected readonly closeSaveLocationMenu = new Subject<void>();
  protected readonly closeNotebookMenu = new Subject<void>();
  private readonly closeNewNotebookDialog = new Subject<void>();
  private readonly initialRailTenantId: string | null;
  private readonly initialNotebookId: string | undefined;
  private readonly initialNotebookName: string | null;
  private readonly selectedNotebookId = signal<string | null>(null);
  private readonly selectedNotebookName = signal<string | null>(null);
  private pendingCreatedNotebook?: { readonly id: string; readonly name: string | null };
  private readonly newNotebookTenantId = signal<string | null>(null);

  /**
   * The team the caller is inside, or `undefined` in a personal workspace / a pending invite. Drives
   * whether the Sharing Header renders at all (#1016 r2 row 2a).
   */
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    this.workspaceIdentityService,
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  /**
   * The tenant a NEW note will be written under — the Sharing Header's current choice.
   *
   * Seeded from the rail's selected section so the dialog opens agreeing with the rail (row 2b), and
   * only ever consulted for a create: an existing note's tenant is its own, see {@link requestConfig}.
   */
  protected readonly sharingTenantId = signal<string | null>(null);

  protected readonly destinationIsTeam = computed(() => {
    const identity = this.workspaceIdentity();
    return !!identity && this.sharingTenantId() === identity.spaceTenantId;
  });

  protected readonly saveLocationButtonLabel = computed(() =>
    this.savedTenantId()
      ? this.collaborationTenantId()
        ? this.collaborationTeamName()
        : 'My Workspace'
      : this.destinationIsTeam()
        ? (this.workspaceIdentity()?.spaceTitle ?? 'Team Workspace')
        : 'My Workspace',
  );

  protected readonly saveLocationButtonWidth = computed(() => (this.destinationIsTeam() ? 157 : 164));

  protected readonly defaultNotebookLabel = computed(() => (this.destinationIsTeam() ? 'Team Notes' : 'My Notes'));

  protected readonly notebookButtonLabel = computed(() => {
    const notebookId = this.selectedNotebookId();
    if (!notebookId) {
      return this.defaultNotebookLabel();
    }

    return (
      this.notebookOptions().find(({ id }) => id === notebookId)?.title ??
      this.selectedNotebookName() ??
      this.defaultNotebookLabel()
    );
  });

  protected readonly notebookButtonWidth = computed(() => {
    if (this.selectedNotebookId()) {
      return null;
    }
    return this.destinationIsTeam() ? 145 : 128;
  });

  protected readonly accessLabel = computed(() =>
    this.destinationIsTeam() ? (this.workspaceIdentity()?.spaceTitle ?? 'Team Workspace') : 'Only me',
  );

  protected readonly teamInitials = computed(() =>
    (this.workspaceIdentity()?.spaceTitle ?? '')
      .split(/\s+/)
      .filter((part) => part.length > 0)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join(''),
  );

  protected readonly locationControlsLocked = computed(() => this.savedInThisForm() || !!this.stitchItem?.id);
  /** The save location cannot change once saved, nor for a new note whose source fixed its tenant (#1447 R3b). */
  protected destinationLocked(): boolean {
    return this.locationControlsLocked() || !!this.stitchItem?.accountId;
  }

  protected override tenantOwnerId(): string | undefined {
    return this.stitchItem?.accountId ?? this.sharingTenantId() ?? undefined;
  }

  /**
   * Names the destination on the primary button (#1016 r2 row 2c), so the flip is visible without
   * re-reading the picker. Outside a team it stays `Save` — there is only one destination.
   */
  protected readonly saveLabel = computed(() =>
    this.editorSubmitInProgress()
      ? 'Saving…'
      : this.savedInThisForm() || !!this.stitchItem?.id
        ? 'Save'
        : 'Create note',
  );

  constructor() {
    super(inject(Injector), inject(NotesService));

    const initialRailTenantId = this.notesTenantScopeService?.effectiveTenantId() ?? null;
    const notesState = this.advancedSearchService.getStateSync()?.[Application.notes];
    this.initialRailTenantId = initialRailTenantId;
    this.initialNotebookId = notesState?.containersIds[0];
    this.initialNotebookName = notesState?.containerName ?? null;

    this.editorActions = [
      { label: 'Search', handler: (text: string) => this.handleSearch(text) },
      { label: 'Make Tag', handler: (text: string) => this.handleMakeTag(text) },
      { label: 'Make Knot', handler: (text: string) => this.handleMakeKnot(text) },
    ];

    // The one seed rule for every adopting form (#1128 review F3): seed from the rail, then RE-SEED on
    // identity delivery with `?? identity.memberTenantId`, so an unresolved rail cannot leave the bound
    // tenant `null` while the control reads "Private to me" — a create would then omit `space_id` and
    // land wherever the surrounding context resolved to. Tasks' shape
    // (`task-form.component.ts`, `project-form.component.ts`), adopted here so the four hosts agree.
    this.sharingTenantId.set(initialRailTenantId);
    this.workspaceIdentityService
      .current()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.notesTenantScopeService?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      });

    inject(DestroyRef).onDestroy(() => {
      this.closeSaveLocationMenu.complete();
      this.retryOwningWorkspace.complete();
      this.closeNotebookMenu.complete();
      this.closeNewNotebookDialog.next();
      this.closeNewNotebookDialog.complete();
    });
  }

  /**
   * The tenant this save is addressed to, as an explicit `OWNING_SPACE_ID` (#1016 r2 rows 2d and 3).
   *
   * Two different questions, and the order matters:
   *
   * - An EXISTING note is written back to the tenant it already lives under (`accountId`, which for
   *   notes IS the tenant column), never to whatever the rail happens to have selected. That covers
   *   both cases row 3 names — a selection changed while the dialog was open, and a note opened from
   *   search or a stitch that belongs to the other section — without a general propagation layer and
   *   without a DTO change: `Stitch` already maps `accountId` off the response.
   * - A NEW note goes where the Sharing Header says, defaulted to the rail's section.
   *
   * `OWNING_SPACE_ID` is url-agnostic and is honoured ahead of the prefix table, so this holds for the
   * create's slash-less URL as well. The token cannot express "no space_id" (`if (owning)` is falsy for
   * `null`), and it does not need to here: inside a team both choices are real tenant ids, and in a
   * personal workspace the header does not render and there is nothing to override — so a null tenant
   * falls back to the inherited `requestContext`, which is what mail's `IGNORE_SPACE_CONTEXT` arm is
   * for elsewhere and is deliberately not duplicated.
   */
  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) {
      return { tenantItems: [this.stitchItem] };
    }

    const tenantId = this.sharingTenantId();
    if (!tenantId) {
      return super.requestConfig();
    }
    return { requestContext: new HttpContext().set(OWNING_SPACE_ID, tenantId) };
  }

  protected readonly noteContentState = signal<'loading' | 'ready' | 'error'>('loading');
  private readonly bodyEditor = viewChild(EditorComponent);
  protected readonly editorSubmitInProgress = signal(false);
  private readonly noteChangeDetector = inject(ChangeDetectorRef);
  private saveCompletion?: Observable<void>;
  private bodyEditedDuringSave = false;
  private submittedNoteValue?: ReturnType<FormGroup<NoteFormGroupConfig>['getRawValue']>;
  private laterNoteValue?: Partial<ReturnType<FormGroup<NoteFormGroupConfig>['getRawValue']>>;

  protected override pendingSave(): Observable<void> | undefined {
    return this.saveCompletion;
  }
  protected readonly editorContentReady = computed(() => this.bodyEditor()?.contentReady() ?? true);

  public override ngOnChanges(changes: SimpleChanges): void {
    if ('note' in changes && !this.preservesDraftOnRefresh()) {
      this.bodyEditor()?.invalidateContent();
      this.historyGeneration++;
      this.cancelHistorySave();
      this.historyRestorePending.set(false);
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
  }

  @warmUpObservable
  public override handleSubmit(emitListUpdate = false): Observable<Note> {
    // All callers, including autosave and the unsaved-changes dialog, flush through this boundary.
    if (this.editorSubmitInProgress() || this.saveInProgress) return EMPTY;
    if (!this.canSubmitCurrentNote()) {
      this.showEditorNotReady();
      return EMPTY;
    }
    const editor = this.bodyEditor();
    const note = this.note;
    const form = this.form;
    this.editorSubmitInProgress.set(true);
    const completed = new ReplaySubject<void>(1);
    this.saveCompletion = completed.asObservable();
    return (editor ? from(editor.flush()) : of({ value: this.form.controls.body.value ?? '', changed: false })).pipe(
      catchError(() => {
        this.noteToasterService.show({
          text: 'Could not read your note. Your text is still here; try Save again.',
          icon: 'sapphire-close-small',
        });
        return EMPTY;
      }),
      switchMap((flushed) => {
        if (!flushed || this.note !== note || this.form !== form || !this.canSubmitCurrentNote()) {
          this.showEditorNotReady();
          return EMPTY;
        }
        // The CVA may already be detached when close-autosave awaits EditorJS.
        if (flushed.changed) {
          form.controls.body.setValue(flushed.value, { emitModelToViewChange: false });
          form.controls.body.markAsDirty();
        }
        this.bodyEditedDuringSave = false;
        this.submittedNoteValue = form.getRawValue();
        return super.handleSubmit(emitListUpdate).pipe(
          tap(() => {
            // The response represents the submitted snapshot, not edits made while it was in flight.
            if (this.form === form && this.laterNoteValue) {
              form.patchValue(this.laterNoteValue);
              for (const key of Object.keys(this.laterNoteValue)) form.get(key)?.markAsDirty();
            }
          }),
        );
      }),
      finalize(() => {
        this.editorSubmitInProgress.set(false);
        this.saveCompletion = undefined;
        this.submittedNoteValue = undefined;
        this.laterNoteValue = undefined;
        this.bodyEditedDuringSave = false;
        // A close can destroy this view; notify after the save/change-detection turn settles.
        queueMicrotask(() => {
          this.noteChangeDetector.markForCheck();
          completed.next();
          completed.complete();
        });
      }),
    );
  }

  private canSubmitCurrentNote(): boolean {
    return (
      this.noteContentState() === 'ready' &&
      this.editorContentReady() &&
      (!this.note.id || this.note.body !== undefined) &&
      (this.form?.controls.id.value ?? null) === (this.note.id ?? null)
    );
  }

  private showEditorNotReady(): void {
    this.noteToasterService.show({
      text: 'Your note is still loading. Wait until the editor is ready, then try again.',
      icon: 'sapphire-close-small',
    });
  }

  public override ngOnInit(): void {
    // The form is the common boundary for main view, panel, modal and Hub callers.
    // A missing body means a summary; an explicitly empty body is loaded content.
    this.stitchItemChanged
      .pipe(
        switchMap(() => {
          const note = this.note;
          if (!note.id || note.body !== undefined) {
            this.noteContentState.set('ready');
            return EMPTY;
          }
          this.noteContentState.set('loading');
          return this.notesService.getItem(note, { tenantItems: [note] }).pipe(
            take(1),
            tap((details) => {
              if (this.note !== note) return;
              if (details.id !== note.id || details.accountId !== note.accountId || details.body === undefined) {
                this.noteContentState.set('error');
                return;
              }
              // A summary's first body load can arrive while another field already has a draft.
              if (this.form && !this.form.controls.body.dirty) {
                this.form.controls.body.setValue(details.body);
                this.originalFormValue = { ...this.originalFormValue, body: details.body };
                this.captureSaveBaseline(['body', 'uploads']);
              }
              Object.assign(note, details);
              this.stitchItemChanged.next();
            }),
            catchError(() => {
              this.noteContentState.set('error');
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe();
    super.ngOnInit();
    this.signalsService
      .getSignal(SignalType.STITCH_EVENT)
      .pipe(
        filter(
          (event) =>
            event.stitchType === StitchType.NOTE &&
            event.eventType === SignalStitchEventType.SNOOZED &&
            event.spaceId === this.note.accountId &&
            event.ids.includes(this.note.id),
        ),
        takeUntil(this.alive),
      )
      .subscribe(() => this.close.emit(true));
    this.creationJourney.set(!this.stitchItem?.id);

    let wasTeam = this.accountKind.isTeam();
    const loginKindSettled = toObservable(this.accountKind.isTeam, { injector: this.injector }).pipe(
      filter((isTeam) => {
        const turnedTeam = isTeam && !wasTeam;
        wasTeam = isTeam;
        return turnedTeam;
      }),
    );
    merge(this.stitchItemChanged, this.itemUpdated, this.retryOwningWorkspace, loginKindSettled)
      .pipe(
        map(() => this.stitchItem),
        switchMap((note) => {
          const tenantId = note?.id ? note.accountId : null;
          this.savedTenantId.set(tenantId ?? null);
          this.savedHasCollaboration.set(!!note?.collaboration);
          this.savedWorkspace.set(undefined);
          this.owningWorkspaceError.set(false);
          if (!tenantId) return of(undefined);
          const ambient = this.workspaceIdentity();
          if (ambient?.spaceTenantId === tenantId) {
            return of(ambient);
          }
          if (ambient?.memberTenantId === tenantId) return of(null);
          if (!this.accountKind.isTeam()) return of(null);
          return this.owningWorkspaceService.resolve(tenantId).pipe(
            catchError(() => {
              this.owningWorkspaceError.set(true);
              return of(undefined);
            }),
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe((identity) => this.savedWorkspace.set(identity));

    let notebookForm: FormGroup<NoteFormGroupConfig> | undefined;
    this.stitchItemChanged
      .pipe(
        map(() => this.form),
        filter((form) => form !== notebookForm),
        switchMap((form) => {
          notebookForm = form;
          return form.controls.notebookId.valueChanges.pipe(startWith(form.controls.notebookId.value));
        }),
        takeUntil(this.alive),
      )
      .subscribe((notebookId) => {
        const createdNotebook = this.pendingCreatedNotebook;
        this.pendingCreatedNotebook = undefined;
        if (!notebookId && createdNotebook) {
          this.selectedNotebookId.set(createdNotebook.id);
          this.selectedNotebookName.set(createdNotebook.name);
          return;
        }
        const previousNotebookId = this.selectedNotebookId();
        this.selectedNotebookId.set(notebookId);

        if (!notebookId) {
          this.selectedNotebookName.set(null);
          return;
        }

        const option = this.notebookOptions().find(({ id }) => id === notebookId);
        if (option) {
          this.selectedNotebookName.set(option.title);
          return;
        }

        if (notebookId === this.initialNotebookId) {
          this.selectedNotebookName.set(this.initialNotebookName);
        } else if (notebookId !== previousNotebookId) {
          this.selectedNotebookName.set(null);
        }
      });

    // Only an unscoped draft inherits the rail; a source's owner and notebook travel together.
    if (
      !this.stitchItem?.id &&
      !this.stitchItem?.accountId &&
      !this.stitchItem?.notebookId &&
      this.initialNotebookId &&
      this.initialRailTenantId === this.sharingTenantId()
    ) {
      this.form.controls.notebookId.setValue(this.initialNotebookId);
      this.selectedNotebookName.set(this.initialNotebookName);
      this.form.controls.notebookId.markAsPristine();
    }
    if (this.selectedNotebookId() && !this.selectedNotebookName()) {
      this.loadNotebooks();
    }

    // A saved note's audience mirrors its item-carried tenant; service methods derive from the item itself.
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      const noteId = this.stitchItem?.id ?? null;
      if (noteId !== this.collaborationNoteId) {
        this.collaborationNoteId = noteId;
        this.resetUnsupportedActivity(this.stitchItem);
        this.collaborationState.set(undefined);
        this.collaborationAccessLost.set(false);
      }
      // A new note that arrives with a tenant has its destination fixed by its source (#1447 R3b: chat).
      if (this.stitchItem?.accountId) {
        this.sharingTenantId.set(this.stitchItem.accountId);
      }
    });

    // A save from elsewhere (another tab, another device) arrives as UPDATED, and the base form
    // merges it into `note` in place. OnPush cannot see a mutation, so "Updated …" kept the old
    // time until a reload (the form's own fields redraw; the timestamps did not). Same as Sheets'
    // B-112.
    this.itemUpdated.pipe(takeUntil(this.alive)).subscribe(() => this.changeDetector.markForCheck());

    this.kpService
      .getSelectedKnots()
      .pipe(
        startWith([]),
        map((knots) => knots.map((k) => k.name)),
        takeUntil(this.alive),
      )
      .subscribe((highlights) => this.highlights.set(highlights));
  }

  protected handleSaveLocationMenuVisible(visible: boolean): void {
    this.saveLocationMenuOpen.set(visible);
    if (visible) {
      this.focusFirstMenuItem('Save location');
    }
  }

  protected closeSaveLocationPanel(): void {
    this.closeSaveLocationMenu.next();
    this.saveLocationTrigger()?.focus();
  }

  protected handleNotebookMenuVisible(visible: boolean): void {
    this.notebookMenuOpen.set(visible);
    if (visible) {
      this.loadNotebooks();
      this.focusFirstMenuItem('Notebook');
    }
  }

  protected selectDestination(tenantId: string): void {
    if (this.destinationLocked() || this.saveInProgress || this.editorSubmitInProgress()) {
      return;
    }

    if (this.sharingTenantId() !== tenantId) {
      this.sharingTenantId.set(tenantId);
      this.initialAssigneeIds.set([]);
      this.initialTeamAccess.set('editor');
      this.form.controls.notebookId.setValue(null);
      this.form.controls.notebookId.markAsDirty();
      this.notebookOptions.set([]);
    }
    this.closeSaveLocationMenu.next();
  }

  protected selectDefaultNotebook(): void {
    this.selectNotebook(null);
  }

  protected selectNotebook(notebook: Notebook | null): void {
    if (this.locationControlsLocked()) {
      return;
    }

    this.form.controls.notebookId.setValue(notebook?.id ?? null);
    this.form.controls.notebookId.markAsDirty();
    this.selectedNotebookName.set(notebook?.title ?? null);
    this.closeNotebookMenu.next();
  }

  protected handleDetailsNotebookSelection(option: DropdownSelectItem<unknown, unknown>): void {
    if (typeof option.value !== 'string' || option.value !== this.form.controls.notebookId.value) {
      return;
    }

    this.selectedNotebookName.set(option.title ?? null);
  }

  protected showNewNotebookDialog(): void {
    if (this.locationControlsLocked()) {
      return;
    }

    this.closeNotebookMenu.next();
    this.newNotebookTitle.reset('');
    this.notebookCreateInProgress.set(false);
    this.newNotebookTenantId.set(this.sharingTenantId());
    this.alertService.show({
      appearance: 'sapphire-modal-custom',
      template: this.newNotebookDialog(),
      width: 340,
      showUntil: this.closeNewNotebookDialog,
    });
  }

  protected cancelNewNotebook(): void {
    if (this.notebookCreateInProgress()) {
      return;
    }
    this.closeNewNotebookDialog.next();
  }

  protected createNewNotebook(): void {
    const title = this.newNotebookTitle.value.trim();
    if (!title || this.notebookCreateInProgress()) {
      return;
    }

    const tenantId = this.newNotebookTenantId();
    this.notebookCreateInProgress.set(true);
    this.notebooksService
      .create(new Notebook({ title }), {
        emit: true,
        toast: false,
        requestContext: tenantId ? new HttpContext().set(OWNING_SPACE_ID, tenantId) : undefined,
      })
      .pipe(take(1), takeUntil(this.alive))
      .subscribe({
        next: (notebook) => {
          this.notebookCreateInProgress.set(false);
          this.form.controls.notebookId.setValue(notebook.id);
          this.form.controls.notebookId.markAsDirty();
          this.selectedNotebookName.set(notebook.title);
          this.notebookOptions.update((items) => [notebook, ...items.filter(({ id }) => id !== notebook.id)]);
          this.closeNewNotebookDialog.next();
          this.loadNotebooks();
        },
        error: () => this.notebookCreateInProgress.set(false),
      });
  }

  protected handleEditorContentEdited(): void {
    this.form.controls.body.markAsDirty();
    if (this.editorSubmitInProgress()) this.bodyEditedDuringSave = true;
  }

  public handleCreateOrSave(): void {
    if (this.editorSubmitInProgress() || this.saveInProgress) return;
    if (!this.canSubmitCurrentNote()) {
      this.showEditorNotReady();
      return;
    }
    if (this.contentReadOnly()) return;
    if (this.stitchItem?.id || !this.destinationIsTeam()) {
      this.handleSubmit();
      return;
    }

    this.confirmTeamCreate();
  }

  protected override performCreate(stitchItem: Note): Observable<Note> {
    const notebookId = stitchItem.notebookId;
    const notebookName =
      this.notebookOptions().find(({ id }) => id === notebookId)?.title ??
      (this.selectedNotebookId() === notebookId ? this.selectedNotebookName() : null);
    if (this.destinationIsTeam()) {
      stitchItem.initialCollaboration = {
        access: this.initialTeamAccess(),
        assigneeIds: [...this.initialAssigneeIds()],
      };
    }
    return this.retainLaterEdits(
      this.notesService.create(stitchItem, { emit: true, toast: false, ...this.requestConfig() }),
    ).pipe(
      tap((savedNote) => {
        if (this.uploadsInProgress() === 0) {
          this.form.controls.uploads.reset([]);
        }
        this.pendingCreatedNotebook = notebookId ? { id: notebookId, name: notebookName } : undefined;
        this.savedInThisForm.set(true);
        this.showSavedToast(savedNote);
      }),
    );
  }

  protected override performUpdate(
    stitchItem: Note,
    emitListUpdate: boolean,
    updatePayload?: StitchRequestConfig['updatePayload'],
  ): Observable<Note> {
    return this.contentReadOnly()
      ? EMPTY
      : this.retainLaterEdits(super.performUpdate(stitchItem, emitListUpdate, updatePayload));
  }

  private retainLaterEdits(request: Observable<Note>): Observable<Note> {
    const form = this.form;
    const submitted = this.submittedNoteValue;
    return request.pipe(
      switchMap((saved) => {
        const editor = this.bodyEditor();
        return (editor && this.bodyEditedDuringSave ? from(editor.flush()) : of(null)).pipe(
          map(() => {
            if (submitted && this.form === form) {
              const latest = form.getRawValue();
              this.laterNoteValue = {};
              for (const key of Object.keys(submitted) as (keyof typeof submitted)[]) {
                const unchanged =
                  key === 'body'
                    ? isEqual(this.bodyBlocks(latest.body), this.bodyBlocks(submitted.body))
                    : isEqual(latest[key], submitted[key]);
                if (!unchanged) Object.assign(this.laterNoteValue, { [key]: latest[key] });
              }
            }
            return saved;
          }),
        );
      }),
    );
  }

  private bodyBlocks(body: string | null | undefined): unknown {
    try {
      const value: unknown = JSON.parse(body ?? '');
      if (value && typeof value === 'object' && 'blocks' in value && Array.isArray(value.blocks)) return value.blocks;
    } catch {
      // Preserve raw comparisons for a legacy or malformed body; only valid editor metadata is ignored.
    }
    return body;
  }

  private loadNotebooks(): void {
    const tenantId = this.stitchItem?.accountId || this.sharingTenantId();
    this.notebookOptions.set([]);

    this.searchNotebooksPage(tenantId, 0)
      .pipe(
        map((response) => ({ ...response, offset: 0 })),
        expand(({ count, items, offset }) => {
          const nextOffset = offset + items.length;
          if (items.length === 0 || nextOffset >= count) {
            return EMPTY;
          }

          return this.searchNotebooksPage(tenantId, nextOffset).pipe(
            map((response) => ({ ...response, offset: nextOffset })),
          );
        }),
        toArray(),
        map((pages) => pages.flatMap(({ items }) => items)),
        takeUntil(this.alive),
      )
      .subscribe((items) => {
        const currentTenantId = this.stitchItem?.accountId || this.sharingTenantId();
        if (currentTenantId !== tenantId) {
          return;
        }

        const availableNotebooks = items.filter(({ archived, deleted }) => !archived && !deleted);
        this.notebookOptions.set(availableNotebooks);
        const selectedNotebook = availableNotebooks.find(({ id }) => id === this.form.controls.notebookId.value);
        if (selectedNotebook) {
          this.selectedNotebookName.set(selectedNotebook.title);
        }
      });
  }

  private searchNotebooksPage(tenantId: string | null, offset: number): Observable<BaseSearchResponse<Notebook>> {
    return this.notebooksService.search(
      { archived: false, deleted: false, limit: 100, offset },
      { requestContext: tenantId ? new HttpContext().set(OWNING_SPACE_ID, tenantId) : undefined },
    );
  }

  private confirmTeamCreate(): void {
    const teamTitle = this.workspaceIdentity()?.spaceTitle ?? 'Team Workspace';
    this.alertService.show({
      appearance: 'sapphire-modal',
      width: 460,
      title: `Create this note in ${teamTitle}?`,
      body: `Everyone at ${teamTitle} will be able to see this note as soon as you create it, and open it from ${this.notebookButtonLabel()}.`,
      rightButtons: [
        { title: 'Cancel', appearance: 'secondary', size: 's', close: true },
        {
          title: 'Create note',
          appearance: 'primary',
          size: 'm',
          close: true,
          click: () => {
            this.handleSubmit();
          },
        },
      ],
    });
  }

  private showSavedToast(savedNote: Note): void {
    const destination = this.saveLocationButtonLabel();
    this.noteToasterService.show({
      text: `Note saved to ${destination}`,
      actions: [{ text: 'View', handler: () => this.viewSavedNote(savedNote) }],
    });
  }

  private viewSavedNote(savedNote: Note): void {
    this.notesTenantScopeService?.selectTenant(this.sharingTenantId());
    const state = this.advancedSearchService.getStateSync();
    state[Application.notes].containersIds = savedNote.notebookId ? [savedNote.notebookId] : [];
    state[Application.notes].containerName = savedNote.notebookId ? this.notebookButtonLabel() : null;
    this.advancedSearchService.setState(state);
    this.handleClose();
    this.notesAppStateService.setMainView(savedNote);
  }

  private focusFirstMenuItem(menuLabel: 'Save location' | 'Notebook'): void {
    afterNextRender(
      () => {
        const menu = this.document.querySelector(`[aria-label="${menuLabel}"]`);
        const firstItem = menu?.querySelector<HTMLElement>(
          '[role^="menuitem"], app-note-access-panel button:not(:disabled), app-note-access-panel section',
        );
        firstItem?.focus();
      },
      { injector: this.injector },
    );
  }

  public handleEditorUploadedAttachment(upload: Upload): void {
    this.form.controls['inlineUploads'].setValue(this.form.controls['inlineUploads'].value.concat(upload));
  }

  get stitchItem(): Note {
    return this.note;
  }

  private handleSearch(text: string): void {
    if (!text) {
      return;
    }
    this.advancedSearchService.search(text);
  }

  private handleMakeTag(text: string): void {
    if (!text) {
      return;
    }
    const tag = new Tag({ name: text, pinned: false, added: true });
    if (this.note?.id) {
      this.tagsService.upsertBulk([tag], [this.note], true, true).subscribe();
    } else {
      this.addToControl(this.form.controls['tags'], tag);
    }
  }

  private handleMakeKnot(text: string): void {
    if (!text) {
      return;
    }
    const knot = new Knot({ name: text, pinned: false, added: true });
    if (this.note?.id) {
      this.knotsService.upsertBulk([knot], [this.note], true, true).subscribe();
    } else {
      this.addToControl(this.form.controls['knots'], knot);
    }
  }

  private addToControl<T extends Tag | Knot>(control: AbstractControl<T[] | null>, item: T): void {
    const current: T[] = control.value || [];
    if (current.some((existing) => Tag.normalizeName(existing.name) === Tag.normalizeName(item.name))) {
      return;
    }
    control.setValue([...current, item]);
    control.markAsDirty();
    control.markAsTouched();
  }

  protected shouldRefreshList(prev, current): boolean {
    return Note.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Note {
    return Note.fromFormGroup(this.form);
  }

  protected asFormGroup(): FormGroup<NoteFormGroupConfig> {
    return this.note.asFormGroup({ maxAttachmentSize: this.maxUploadSize() });
  }
  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.note);
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.note,
      noun: 'Notes',
      collection: this.footerBadge()?.name,
      archived: this.form?.controls.archived.value ?? false,
      deleted: this.form?.controls.deleted.value ?? false,
    };
  }
  protected readonly openItemComments = (): void => {
    void this.selectHistoryTab('comments');
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.note,
      noun: 'note',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
    };
  }
}
